import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/lib/api.ts'
import {
  Search, Layers, ChevronRight, Loader2,
  CheckCircle2, Wand2, ThumbsUp, ThumbsDown,
  BookOpen, CheckCheck, X, Database,
} from 'lucide-react'
import { cn } from '@/lib/utils.ts'
import { DataTable, EmptyState, ErrorState, type ColumnDef } from '@/components/ui'
import type { ArticleGroup } from '../shared.ts'
import { formatCurrency } from '../shared.ts'

interface CspArticle {
  itemCode: string
  itemName: string
  salesPrice: number
  unit: string
  group: string | null
  uomGroupEntry: number | null
  salesUnit: string | null
  parsedDuration: string | null
  parsedBillingCycle: string | null
  priceEntries: unknown[]
}

const cspArticleColumns: ColumnDef<CspArticle, unknown>[] = [
  {
    accessorKey: 'itemCode',
    header: 'ItemCode',
    cell: ({ row }) => <span className="font-mono text-gray-600 whitespace-nowrap">{row.original.itemCode}</span>,
  },
  {
    accessorKey: 'itemName',
    header: 'ItemName',
    cell: ({ row }) => (
      <span className="text-gray-900 max-w-xs truncate block" title={row.original.itemName}>
        {row.original.itemName}
      </span>
    ),
  },
  {
    accessorKey: 'salesPrice',
    header: 'VK',
    cell: ({ row }) => (
      <span className="block text-right font-medium text-gray-700 whitespace-nowrap tabular-nums">
        {formatCurrency(row.original.salesPrice)}
      </span>
    ),
  },
  {
    accessorKey: 'unit',
    header: 'Einheit',
    cell: ({ row }) => <span className="text-gray-500">{row.original.unit}</span>,
  },
  {
    accessorKey: 'group',
    header: 'Gruppe',
    cell: ({ row }) => (
      <span className="text-gray-500 truncate max-w-[120px] block" title={row.original.group ?? ''}>
        {row.original.group ?? '–'}
      </span>
    ),
  },
  {
    accessorKey: 'uomGroupEntry',
    header: 'ME-Grp',
    cell: ({ row }) =>
      row.original.uomGroupEntry != null ? (
        <span className="px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded">{row.original.uomGroupEntry}</span>
      ) : (
        <span className="text-gray-300">–</span>
      ),
  },
  {
    accessorKey: 'salesUnit',
    header: 'VerkaufsME',
    cell: ({ row }) => <span className="font-mono text-gray-500">{row.original.salesUnit ?? '–'}</span>,
  },
  {
    accessorKey: 'parsedDuration',
    header: 'Laufzeit',
    cell: ({ row }) =>
      row.original.parsedDuration ? (
        <span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded">{row.original.parsedDuration}</span>
      ) : (
        <span className="text-gray-300">–</span>
      ),
  },
  {
    accessorKey: 'parsedBillingCycle',
    header: 'Abrechnung',
    cell: ({ row }) =>
      row.original.parsedBillingCycle ? (
        <span className="px-1.5 py-0.5 bg-green-100 text-green-700 rounded">{row.original.parsedBillingCycle}</span>
      ) : (
        <span className="text-gray-300">–</span>
      ),
  },
  {
    accessorKey: 'priceEntries',
    header: 'Preislisten',
    cell: ({ row }) => (
      <span className="block text-right text-gray-500">
        {row.original.priceEntries.length > 0 ? row.original.priceEntries.length : '–'}
      </span>
    ),
  },
]

// ─── Content Standard Section ────────────────────────────────────────────────

interface ContentProposal {
  id: string
  articleId: string
  proposedName: string | null
  proposedDescription: string | null
  originalName: string
  originalDescription: string | null
  categoryRule: string | null
  status: 'pending' | 'approved' | 'rejected'
  reviewedAt: string | null
  createdAt: string
  articleItemCode: string
  articleManufacturer: string | null
  articleGroupName: string | null
}

interface ContentStats {
  raw: number
  proposed: number
  approved: number
  pendingProposals: number
}

interface ContentRule {
  categoryName: string
  descriptionType: 'minimal' | 'standard' | 'sales'
  titleFormat: string
  titleExample: string
  titleBadExample?: string
  descriptionGuidelines: string
  descriptionExample?: string
  titleMaxLength: number
  descriptionMaxLength: number
}

interface GenerateResult {
  generated: number
  skipped: number
  errors: string[]
}

interface CspArticleAnalysis {
  itemCode: string
  itemName: string
  salesPrice: number
  unit: string
  group: string | null
  sapGroupCode: number | null
  uomGroupEntry: number | null
  salesUnit: string | null
  inventoryUOM: string | null
  priceEntries: Array<{ priceList: number; price: number; uomEntry?: string }>
  parsedDuration: string | null
  parsedBillingCycle: string | null
}

interface CspAnalysisResult {
  summary: {
    totalArticles: number
    uniqueBaseProducts: number
    uomGroups: number[]
    salesUnits: string[]
    units: string[]
    sapGroups: string[]
    multiVariantProducts: Array<{
      baseCode: string
      variants: Array<{
        itemCode: string
        itemName: string
        salesPrice: number
        unit: string
        parsedDuration: string | null
        parsedBillingCycle: string | null
        uomGroupEntry: number | null
        salesUnit: string | null
      }>
    }>
  }
  articles: CspArticleAnalysis[]
  productGroups: Record<string, CspArticleAnalysis[]>
}

export function ContentStandardSection() {
  const queryClient = useQueryClient()
  const [showRules, setShowRules] = useState(false)
  const [showCspAnalysis, setShowCspAnalysis] = useState(false)
  const [cspSearch, setCspSearch] = useState('Microsoft')
  const [cspSearchInput, setCspSearchInput] = useState('Microsoft')
  const [statusFilter, setStatusFilter] = useState<'pending' | 'approved' | 'rejected'>('pending')

  const { data: stats } = useQuery<ContentStats>({
    queryKey: ['content-stats'],
    queryFn: () => apiClient.get('/articles/content/stats').then((r) => r.data),
    retry: false,
  })

  const { data: proposals = [], isLoading: proposalsLoading } = useQuery<ContentProposal[]>({
    queryKey: ['content-proposals', statusFilter],
    queryFn: () => apiClient.get('/articles/content/proposals', { params: { status: statusFilter } }).then((r) => r.data),
    retry: false,
  })

  const { data: rules = [] } = useQuery<ContentRule[]>({
    queryKey: ['content-rules'],
    queryFn: () => apiClient.get('/articles/content/rules').then((r) => r.data),
    retry: false,
    enabled: showRules,
  })

  const { data: cspAnalysis, isLoading: cspLoading } = useQuery<CspAnalysisResult>({
    queryKey: ['csp-analysis', cspSearch],
    queryFn: () => apiClient.get('/articles/content/analyze-csp', { params: { q: cspSearch } }).then((r) => r.data),
    retry: false,
    enabled: showCspAnalysis,
  })

  const generateMutation = useMutation<GenerateResult, Error, { groupId?: string; overwriteExisting?: boolean }>({
    mutationFn: (params) => apiClient.post('/articles/content/generate', params).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['content-proposals'] })
      queryClient.invalidateQueries({ queryKey: ['content-stats'] })
    },
  })

  const approveMutation = useMutation({
    mutationFn: (id: string) => apiClient.post(`/articles/content/proposals/${id}/approve`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['content-proposals'] })
      queryClient.invalidateQueries({ queryKey: ['content-stats'] })
      queryClient.invalidateQueries({ queryKey: ['articles'] })
    },
  })

  const rejectMutation = useMutation({
    mutationFn: (id: string) => apiClient.post(`/articles/content/proposals/${id}/reject`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['content-proposals'] })
      queryClient.invalidateQueries({ queryKey: ['content-stats'] })
    },
  })

  const approveAllMutation = useMutation({
    mutationFn: () => apiClient.post('/articles/content/proposals/approve-all'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['content-proposals'] })
      queryClient.invalidateQueries({ queryKey: ['content-stats'] })
      queryClient.invalidateQueries({ queryKey: ['articles'] })
    },
  })

  const { data: groups = [] } = useQuery<ArticleGroup[]>({
    queryKey: ['article-groups'],
    queryFn: () => apiClient.get('/articles/groups').then((r) => r.data),
    retry: false,
  })

  return (
    <div className="space-y-4">
      {/* Stats Overview */}
      {stats && (
        <div className="grid grid-cols-4 gap-3">
          {[
            { label: 'Unbearbeitet', value: stats.raw, color: 'text-gray-600', bg: 'bg-gray-50 border-gray-200' },
            { label: 'Vorschläge offen', value: stats.pendingProposals, color: 'text-amber-600', bg: 'bg-amber-50 border-amber-200' },
            { label: 'Vorgeschlagen', value: stats.proposed, color: 'text-blue-600', bg: 'bg-blue-50 border-blue-200' },
            { label: 'Freigegeben', value: stats.approved, color: 'text-green-600', bg: 'bg-green-50 border-green-200' },
          ].map((s) => (
            <div key={s.label} className={cn('border rounded-lg p-3', s.bg)}>
              <p className="text-xs font-medium text-gray-500">{s.label}</p>
              <p className={cn('text-2xl font-bold', s.color)}>{s.value}</p>
            </div>
          ))}
        </div>
      )}

      {/* Actions */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
            <Wand2 className="w-4 h-4 text-brand-500" />
            Content-Standardisierung
          </h3>
          <button
            onClick={() => setShowRules(!showRules)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-600 transition-colors"
          >
            <BookOpen className="w-3.5 h-3.5" />
            {showRules ? 'Regeln ausblenden' : 'Regeln anzeigen'}
          </button>
        </div>
        <p className="text-xs text-gray-500 mb-3">
          Generiere einheitliche Titel und Beschreibungen für alle Artikel basierend auf dem zentralen Content-Standard.
          Die KI nutzt Muster-Texte aus dem Wissensspeicher als Stil-Vorlage und gibt Vorschläge mit hoher Sicherheit automatisch frei.
        </p>
        <Link
          to="/einstellungen?tab=wissensspeicher"
          className="inline-flex items-center gap-1.5 text-xs text-violet-600 hover:text-violet-800 mb-4"
        >
          <Database className="w-3 h-3" />
          Muster-Texte im Wissensspeicher verwalten →
        </Link>

        <div className="flex items-center gap-3">
          <button
            onClick={() => generateMutation.mutate({})}
            disabled={generateMutation.isPending}
            className="flex items-center gap-2 px-4 py-2 bg-brand-500 hover:bg-brand-600 disabled:bg-brand-300 text-white text-sm font-medium rounded-lg transition-colors"
          >
            {generateMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
            {generateMutation.isPending ? 'Generiere...' : 'Alle unbearbeiteten standardisieren'}
          </button>

          {/* Per-group generation */}
          <select
            onChange={(e) => {
              if (e.target.value) {
                generateMutation.mutate({ groupId: e.target.value })
                e.target.value = ''
              }
            }}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-brand-500 focus:border-transparent"
            disabled={generateMutation.isPending}
          >
            <option value="">Einzelne Gruppe...</option>
            {groups.map((g) => (
              <option key={g.id} value={g.id}>{g.name}</option>
            ))}
          </select>
        </div>

        {generateMutation.isSuccess && (
          <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700 flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
            {generateMutation.data.generated} Vorschläge generiert, {generateMutation.data.skipped} übersprungen
            {generateMutation.data.errors.length > 0 && (
              <span className="text-amber-600"> ({generateMutation.data.errors.length} Fehler)</span>
            )}
          </div>
        )}

        {generateMutation.isError && (
          <ErrorState
            size="sm"
            className="mt-3"
            description={(generateMutation.error as Error).message || 'Fehler bei der Generierung'}
            details={generateMutation.error ? JSON.stringify(generateMutation.error, Object.getOwnPropertyNames(generateMutation.error as object), 2) : undefined}
          />
        )}
      </div>

      {/* Rules Reference */}
      {showRules && rules.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-3">
          <h4 className="text-sm font-semibold text-gray-900">Content-Standard Regeln</h4>
          {rules.map((rule) => (
            <div key={rule.categoryName} className="border border-gray-100 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-sm font-semibold text-gray-900">{rule.categoryName}</span>
                <span className={cn(
                  'px-2 py-0.5 text-xs font-medium rounded-full',
                  rule.descriptionType === 'sales' ? 'bg-green-100 text-green-700' :
                  rule.descriptionType === 'standard' ? 'bg-blue-100 text-blue-700' :
                  'bg-gray-100 text-gray-600',
                )}>
                  {rule.descriptionType === 'sales' ? 'Verkaufsfördernd' :
                   rule.descriptionType === 'standard' ? 'Standard' : 'Minimal'}
                </span>
              </div>
              <div className="text-xs space-y-1 text-gray-600">
                <p><span className="font-medium text-gray-700">Format:</span> {rule.titleFormat}</p>
                <p><span className="font-medium text-green-700">Richtig:</span> {rule.titleExample}</p>
                {rule.titleBadExample && (
                  <p><span className="font-medium text-red-600">Falsch:</span> {rule.titleBadExample}</p>
                )}
                <p className="text-gray-500">{rule.descriptionGuidelines}</p>
                {rule.descriptionExample && (
                  <p className="italic text-gray-500 border-l-2 border-gray-300 pl-2 mt-1">{rule.descriptionExample}</p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* CSP Article Analysis */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <button
          onClick={() => setShowCspAnalysis(!showCspAnalysis)}
          className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-center gap-2">
            <Search className="w-4 h-4 text-purple-500" />
            <h3 className="text-sm font-semibold text-gray-900">Artikel-Struktur analysieren</h3>
            <span className="text-xs text-gray-400">(CSP / Laufzeit-Varianten)</span>
          </div>
          <ChevronRight className={cn('w-4 h-4 text-gray-400 transition-transform', showCspAnalysis && 'rotate-90')} />
        </button>

        {showCspAnalysis && (
          <div className="border-t border-gray-200 p-4 space-y-4">
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={cspSearchInput}
                onChange={(e) => setCspSearchInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    setCspSearch(cspSearchInput)
                  }
                }}
                placeholder="Suchbegriff (z.B. Microsoft, SonicWall, Managed)..."
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-brand-500 focus:border-transparent"
              />
              <button
                onClick={() => setCspSearch(cspSearchInput)}
                disabled={cspLoading}
                className="flex items-center gap-1.5 px-4 py-2 bg-purple-500 hover:bg-purple-600 disabled:bg-purple-300 text-white text-sm font-medium rounded-lg transition-colors"
              >
                {cspLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                Analysieren
              </button>
            </div>

            {cspLoading && (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-5 h-5 animate-spin text-purple-500" />
              </div>
            )}

            {cspAnalysis && !cspLoading && (
              <div className="space-y-4">
                {/* Summary stats */}
                <div className="grid grid-cols-4 gap-3">
                  <div className="border border-purple-200 bg-purple-50 rounded-lg p-3">
                    <p className="text-xs font-medium text-gray-500">Artikel gefunden</p>
                    <p className="text-2xl font-bold text-purple-600">{cspAnalysis.summary.totalArticles}</p>
                  </div>
                  <div className="border border-blue-200 bg-blue-50 rounded-lg p-3">
                    <p className="text-xs font-medium text-gray-500">Basis-Produkte</p>
                    <p className="text-2xl font-bold text-blue-600">{cspAnalysis.summary.uniqueBaseProducts}</p>
                  </div>
                  <div className="border border-amber-200 bg-amber-50 rounded-lg p-3">
                    <p className="text-xs font-medium text-gray-500">Multi-Varianten</p>
                    <p className="text-2xl font-bold text-amber-600">{cspAnalysis.summary.multiVariantProducts.length}</p>
                  </div>
                  <div className="border border-green-200 bg-green-50 rounded-lg p-3">
                    <p className="text-xs font-medium text-gray-500">ME-Gruppen</p>
                    <p className="text-lg font-bold text-green-600">{cspAnalysis.summary.uomGroups.length > 0 ? cspAnalysis.summary.uomGroups.join(', ') : '–'}</p>
                  </div>
                </div>

                {/* Sales Units & SAP Groups */}
                {(cspAnalysis.summary.salesUnits.length > 0 || cspAnalysis.summary.sapGroups.length > 0) && (
                  <div className="grid grid-cols-2 gap-3">
                    {cspAnalysis.summary.salesUnits.length > 0 && (
                      <div className="border border-gray-200 rounded-lg p-3">
                        <p className="text-xs font-medium text-gray-500 mb-1">VerkaufsME</p>
                        <div className="flex flex-wrap gap-1">
                          {cspAnalysis.summary.salesUnits.map((u) => (
                            <span key={u} className="px-2 py-0.5 bg-gray-100 text-xs font-mono rounded">{u}</span>
                          ))}
                        </div>
                      </div>
                    )}
                    {cspAnalysis.summary.sapGroups.length > 0 && (
                      <div className="border border-gray-200 rounded-lg p-3">
                        <p className="text-xs font-medium text-gray-500 mb-1">SAP-Gruppen</p>
                        <div className="flex flex-wrap gap-1">
                          {cspAnalysis.summary.sapGroups.map((g) => (
                            <span key={g} className="px-2 py-0.5 bg-gray-100 text-xs rounded">{g}</span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Multi-Variant Products */}
                {cspAnalysis.summary.multiVariantProducts.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-2">
                      <Layers className="w-4 h-4 text-amber-500" />
                      Produkte mit mehreren Varianten
                    </h4>
                    <div className="space-y-2">
                      {cspAnalysis.summary.multiVariantProducts.map((mp) => (
                        <div key={mp.baseCode} className="border border-amber-200 bg-amber-50/50 rounded-lg p-3">
                          <p className="text-xs font-mono font-medium text-amber-700 mb-2">{mp.baseCode}</p>
                          <div className="space-y-1">
                            {mp.variants.map((v) => (
                              <div key={v.itemCode} className="flex items-center gap-3 text-xs bg-white rounded px-2 py-1.5 border border-amber-100">
                                <span className="font-mono text-gray-500 w-48 flex-shrink-0 truncate" title={v.itemCode}>{v.itemCode}</span>
                                <span className="flex-1 truncate text-gray-800" title={v.itemName}>{v.itemName}</span>
                                <span className="font-medium text-gray-700 w-20 text-right">{formatCurrency(v.salesPrice)}</span>
                                <span className="text-gray-500 w-16">{v.unit}</span>
                                {v.parsedDuration && (
                                  <span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded text-xs flex-shrink-0">{v.parsedDuration}</span>
                                )}
                                {v.parsedBillingCycle && (
                                  <span className="px-1.5 py-0.5 bg-green-100 text-green-700 rounded text-xs flex-shrink-0">{v.parsedBillingCycle}</span>
                                )}
                                {v.salesUnit && (
                                  <span className="px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded text-xs flex-shrink-0 font-mono">{v.salesUnit}</span>
                                )}
                                {v.uomGroupEntry != null && (
                                  <span className="px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded text-xs flex-shrink-0">ME:{v.uomGroupEntry}</span>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* All Articles Table */}
                <div>
                  <h4 className="text-sm font-semibold text-gray-900 mb-2">Alle Artikel ({cspAnalysis.articles.length})</h4>
                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <div className="overflow-x-auto max-h-96 overflow-y-auto">
                      <DataTable<CspArticle>
                        data={cspAnalysis.articles as CspArticle[]}
                        columns={cspArticleColumns}
                        getRowId={(a) => a.itemCode}
                        enableSorting={false}
                        emptyState={<EmptyState size="sm" title="Keine Artikel" />}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {!cspLoading && cspAnalysis && cspAnalysis.articles.length === 0 && (
              <div className="text-center py-8 text-sm text-gray-400">
                Keine Artikel gefunden für &quot;{cspSearch}&quot;
              </div>
            )}
          </div>
        )}
      </div>

      {/* Proposals Review */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center gap-2">
            <h4 className="text-sm font-semibold text-gray-900">Vorschläge</h4>
            <div className="flex gap-1">
              {(['pending', 'approved', 'rejected'] as const).map((s) => (
                <button
                  key={s}
                  onClick={() => setStatusFilter(s)}
                  className={cn(
                    'px-2.5 py-1 text-xs font-medium rounded-lg transition-colors',
                    statusFilter === s ? 'bg-brand-500 text-white' : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200',
                  )}
                >
                  {s === 'pending' ? 'Offen' : s === 'approved' ? 'Freigegeben' : 'Abgelehnt'}
                </button>
              ))}
            </div>
          </div>
          {statusFilter === 'pending' && proposals.length > 0 && (
            <button
              onClick={() => approveAllMutation.mutate()}
              disabled={approveAllMutation.isPending}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-green-500 hover:bg-green-600 text-white transition-colors"
            >
              <CheckCheck className="w-3.5 h-3.5" />
              {approveAllMutation.isPending ? 'Übernehme...' : 'Alle übernehmen'}
            </button>
          )}
        </div>

        {proposalsLoading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-5 h-5 animate-spin text-brand-500" />
          </div>
        )}

        {!proposalsLoading && proposals.length === 0 && (
          <div className="text-center py-12 text-sm text-gray-400">
            {statusFilter === 'pending'
              ? 'Keine offenen Vorschläge. Starte die Standardisierung oben.'
              : statusFilter === 'approved'
              ? 'Noch keine freigegebenen Vorschläge.'
              : 'Keine abgelehnten Vorschläge.'
            }
          </div>
        )}

        <div className="divide-y divide-gray-100">
          {proposals.map((p) => (
            <div key={p.id} className="p-4 hover:bg-gray-50 transition-colors">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  {/* Header */}
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs font-mono text-gray-400">{p.articleItemCode}</span>
                    {p.categoryRule && (
                      <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-brand-50 text-brand-700">
                        {p.categoryRule}
                      </span>
                    )}
                    {p.articleManufacturer && (
                      <span className="text-xs text-gray-500">{p.articleManufacturer}</span>
                    )}
                  </div>

                  {/* Title comparison */}
                  <div className="space-y-1.5">
                    <div className="flex items-start gap-2">
                      <span className="text-xs font-medium text-red-500 mt-0.5 flex-shrink-0 w-12">Vorher:</span>
                      <p className="text-sm text-gray-500 line-through">{p.originalName}</p>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-xs font-medium text-green-600 mt-0.5 flex-shrink-0 w-12">Nachher:</span>
                      <p className="text-sm font-medium text-gray-900">{p.proposedName || p.originalName}</p>
                    </div>
                  </div>

                  {/* Description */}
                  {p.proposedDescription && (
                    <div className="mt-2 p-2.5 bg-blue-50 border border-blue-100 rounded-lg">
                      <p className="text-xs font-medium text-blue-600 mb-1">Beschreibung:</p>
                      <p className="text-xs text-gray-700">{p.proposedDescription}</p>
                    </div>
                  )}

                  {p.originalDescription && !p.proposedDescription && (
                    <div className="mt-2 p-2.5 bg-gray-50 border border-gray-100 rounded-lg">
                      <p className="text-xs font-medium text-gray-500 mb-1">Bestehende Beschreibung (bleibt):</p>
                      <p className="text-xs text-gray-600">{p.originalDescription}</p>
                    </div>
                  )}
                </div>

                {/* Actions */}
                {p.status === 'pending' && (
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    <button
                      onClick={() => approveMutation.mutate(p.id)}
                      disabled={approveMutation.isPending}
                      title="Übernehmen"
                      className="p-2 rounded-lg bg-green-50 hover:bg-green-100 text-green-600 transition-colors"
                    >
                      <ThumbsUp className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => rejectMutation.mutate(p.id)}
                      disabled={rejectMutation.isPending}
                      title="Ablehnen"
                      className="p-2 rounded-lg bg-red-50 hover:bg-red-100 text-red-500 transition-colors"
                    >
                      <ThumbsDown className="w-4 h-4" />
                    </button>
                  </div>
                )}

                {p.status === 'approved' && (
                  <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0" />
                )}

                {p.status === 'rejected' && (
                  <X className="w-5 h-5 text-red-400 flex-shrink-0" />
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
